// src/pages/negocio/PerfilNegocio.js
//
// Perfil del negocio: datos generales editables + gestión de
// domiciliarios (pre-registro modelo B).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Grid, Alert, CircularProgress,
  TextField, Button, Chip, Switch, FormControlLabel, ToggleButton,
  ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Divider, Tab, Tabs,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon   from '@mui/icons-material/Edit';

import { perfilNegocio } from '../../api/perfilApi';
import SelectorMunicipio from '../../components/registro/SelectorMunicipio';
import CampoUbicacionGPS from '../../components/registro/CampoUbicacionGPS';

const DIAS = [
  { k: 'L', label: 'Lun' }, { k: 'M', label: 'Mar' }, { k: 'X', label: 'Mié' },
  { k: 'J', label: 'Jue' }, { k: 'V', label: 'Vie' }, { k: 'S', label: 'Sáb' },
  { k: 'D', label: 'Dom' },
];

export default function PerfilNegocio() {
  const [tab, setTab] = useState(0);
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Perfil</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Tu negocio</Typography>
      </Box>
      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Datos generales" />
          <Tab label="Domiciliarios" />
        </Tabs>
      </Card>
      {tab === 0 && <TabDatos />}
      {tab === 1 && <TabDomiciliarios />}
    </Stack>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 1: Datos generales
// ═══════════════════════════════════════════════════════════

function TabDatos() {
  const [perfil, setPerfil]     = useState(null);
  const [borrador, setBorrador] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEdit]     = useState(false);
  const [error, setError]       = useState('');
  const [ok, setOk]             = useState('');

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await perfilNegocio.obtener();
      setPerfil(r); setBorrador(r);
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar el perfil.'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const upd = (k) => (e) => setBorrador(s => ({ ...s, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));

  const toggleDia = (i) => {
    const arr = (borrador.diasAtencion || '1111100').padEnd(7, '0').split('');
    arr[i] = arr[i] === '1' ? '0' : '1';
    setBorrador(s => ({ ...s, diasAtencion: arr.join('') }));
  };

  const guardar = async () => {
    setGuardando(true); setError(''); setOk('');
    try {
      const payload = {
        nombre:             borrador.nombre,
        direccion:          borrador.direccion,
        detalleEntrega:     borrador.detalleEntrega || null,
        barrio:             borrador.barrio || null,
        telefono:           borrador.telefono,
        horario:            borrador.horario || null,
        color:              borrador.color || null,
        latitud:            borrador.latitud ?? null,
        longitud:           borrador.longitud ?? null,
        municipioID:        parseInt(borrador.municipioID, 10),
        diasAtencion:       borrador.diasAtencion || null,
        horaApertura:       borrador.horaApertura || null,
        horaCierre:         borrador.horaCierre || null,
        minutosCompromisoAtencion: parseInt(borrador.minutosCompromisoAtencion, 10) || 15,
        aceptaFiado:        !!borrador.aceptaFiado,
      };
      const r = await perfilNegocio.actualizar(payload);
      setPerfil(r); setBorrador(r); setEdit(false);
      setOk('Datos actualizados.');
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo actualizar.'); }
    finally { setGuardando(false); }
  };

  if (cargando) return <CircularProgress />;
  if (!perfil) return <Alert severity="error">No se pudo cargar el perfil.</Alert>;

  return (
    <Card>
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}

        {!editando ? (
          <>
            <Grid container spacing={2}>
              <Info label="Nombre" value={perfil.nombre} />
              <Info label="Categoría" value={perfil.categoriaNombre || '—'} />
              <Info label="Teléfono" value={perfil.telefono || '—'} />
              <Info label="Correo" value={perfil.correo || '—'} />
              <Info label="Municipio" value={perfil.municipio || '—'} />
              <Info label="Compromiso" value={`${perfil.minutosCompromisoAtencion || 15} min`} />
              <Info label="Dirección" value={perfil.direccion || '—'} full />
              <Info label="Detalle" value={perfil.detalleEntrega || '—'} />
              <Info label="Barrio" value={perfil.barrio || '—'} />
              <Info label="Horario" value={perfil.horario || '—'} full />
              <Grid item xs={12}>
                <Chip
                  label={perfil.aceptaFiado ? '✓ Acepta fiado' : '✗ No acepta fiado'}
                  color={perfil.aceptaFiado ? 'warning' : 'default'}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <Button startIcon={<EditIcon />} variant="outlined" sx={{ mt: 3 }} onClick={() => setEdit(true)}>
              Editar
            </Button>
          </>
        ) : (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Nombre" value={borrador.nombre || ''} onChange={upd('nombre')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Teléfono" value={borrador.telefono || ''} onChange={upd('telefono')} />
              </Grid>
            </Grid>
            <SelectorMunicipio
              value={borrador.municipioID}
              onChange={(v) => setBorrador(s => ({ ...s, municipioID: v }))}
            />
            <TextField fullWidth label="Dirección" value={borrador.direccion || ''} onChange={upd('direccion')} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Detalle" value={borrador.detalleEntrega || ''} onChange={upd('detalleEntrega')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Barrio" value={borrador.barrio || ''} onChange={upd('barrio')} />
              </Grid>
            </Grid>

            <Divider>Horario</Divider>
            <ToggleButtonGroup size="small">
              {DIAS.map((d, i) => (
                <ToggleButton
                  key={d.k} value={d.k}
                  selected={(borrador.diasAtencion || '1111100').padEnd(7, '0')[i] === '1'}
                  onChange={() => toggleDia(i)}
                >
                  {d.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth type="time" label="Apertura"
                  value={borrador.horaApertura || ''}
                  onChange={upd('horaApertura')}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="time" label="Cierre"
                  value={borrador.horaCierre || ''}
                  onChange={upd('horaCierre')}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>

            <Divider>Preferencias</Divider>
            <TextField
              type="number" label="Minutos que prometes para responder"
              value={borrador.minutosCompromisoAtencion || 15}
              onChange={upd('minutosCompromisoAtencion')}
              helperText="Se muestra a los vecis en tu tarjeta del directorio."
              inputProps={{ min: 1, max: 240 }}
            />
            <FormControlLabel
              control={<Switch checked={!!borrador.aceptaFiado} onChange={upd('aceptaFiado')} />}
              label={borrador.aceptaFiado ? 'Acepto fiado a mis vecis' : 'No acepto fiado'}
            />

            <TextField fullWidth type="color" label="Color distintivo"
              value={borrador.color || '#0F4C3A'}
              onChange={upd('color')}
              sx={{ maxWidth: 200 }}
              InputLabelProps={{ shrink: true }} />

            <CampoUbicacionGPS
              lat={borrador.latitud} lng={borrador.longitud}
              onChange={({ lat, lng }) => setBorrador(s => ({ ...s, latitud: lat, longitud: lng }))}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => { setBorrador(perfil); setEdit(false); }}>Cancelar</Button>
              <Button variant="contained" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function Info({ label, value, full = false }) {
  return (
    <Grid item xs={12} md={full ? 12 : 6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>{value}</Typography>
    </Grid>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 2: Domiciliarios (pre-registro modelo B)
// ═══════════════════════════════════════════════════════════

function TabDomiciliarios() {
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [ok, setOk]             = useState('');
  const [dialogo, setDialogo]   = useState(null); // { nombre, apellido, telefono }
  const [borrando, setBorrando] = useState(null);

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await perfilNegocio.domiciliariosListar();
      setItems(Array.isArray(r) ? r : (r.items || []));
    } catch (e) { setError(e.mensajeUsuario || 'No se pudieron cargar los domiciliarios.'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    try {
      await perfilNegocio.domiciliarioAgregar({
        nombre:   dialogo.nombre.trim(),
        apellido: dialogo.apellido.trim(),
        telefono: dialogo.telefono,
      });
      setDialogo(null); setOk('Domiciliario pre-registrado. Debe completar el registro desde su celular.');
      cargar();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo agregar.'); }
  };

  const eliminar = async () => {
    try {
      await perfilNegocio.domiciliarioEliminar(borrando.id || borrando.Id);
      setBorrando(null); setOk('Domiciliario eliminado.');
      cargar();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo eliminar.'); }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Pre-registra a tus domiciliarios acá; ellos completan el registro desde su celular con el teléfono que les asignes.
          </Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialogo({ nombre: '', apellido: '', telefono: '' })}>
            Nuevo
          </Button>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {ok && <Alert severity="success" onClose={() => setOk('')} sx={{ mb: 2 }}>{ok}</Alert>}

        {cargando ? (
          <CircularProgress />
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No has agregado domiciliarios.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {items.map((d) => (
              <Box key={d.id || d.Id} sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 2, borderRadius: 1, bgcolor: '#FBF7F0',
              }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>{d.nombreCompleto || `${d.nombre} ${d.apellido || ''}`}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.telefono || d.Telefono} · {d.tieneAcceso || d.TieneAcceso ? 'Activo' : 'Sin completar registro'}
                  </Typography>
                </Box>
                <IconButton size="small" color="error" onClick={() => setBorrando(d)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>

      <Dialog open={!!dialogo} onClose={() => setDialogo(null)} fullWidth maxWidth="xs">
        <DialogTitle>Nuevo domiciliario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" value={dialogo?.nombre || ''}
              onChange={(e) => setDialogo(s => ({ ...s, nombre: e.target.value }))} />
            <TextField label="Apellido" value={dialogo?.apellido || ''}
              onChange={(e) => setDialogo(s => ({ ...s, apellido: e.target.value }))} />
            <TextField label="Teléfono (con +57)" value={dialogo?.telefono || ''}
              onChange={(e) => setDialogo(s => ({ ...s, telefono: e.target.value }))}
              helperText="Es el número con el que él completará su registro." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogo(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}
            disabled={!dialogo?.nombre || !dialogo?.apellido || !dialogo?.telefono}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!borrando} onClose={() => setBorrando(null)}>
        <DialogTitle>Eliminar domiciliario</DialogTitle>
        <DialogContent>
          <Typography>¿Seguro que quieres eliminar a este domiciliario?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBorrando(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={eliminar}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
